<template>
  <div class="sci-view-container">
    <!-- Header -->
    <header class="sci-header">
      <div class="sci-header-left">
        <button class="sci-btn-back" @click="home">
          <i class="fas fa-arrow-left"></i>
          <span>{{ $t('gestionale') || 'Gestionale' }}</span>
        </button>
        <div class="sci-title-group">
          <h1 class="sci-title">
            <i class="fas fa-boxes me-2 text-primary"></i>
            {{ $t('magazzino') || 'Gestione Magazzino & Giacenze' }}
          </h1>
          <p class="sci-subtitle">Controllo scorte, soglie minime di riordino e valorizzazione inventario</p>
        </div>
      </div>
      <div class="sci-header-right">
        <router-link to="/prodottidacaricare" class="sci-btn sci-btn-primary">
          <i class="fas fa-truck-loading me-1"></i>
          <span>Carico Merci & Fatture</span>
        </router-link>
        <button class="sci-btn sci-btn-secondary" @click="displayProduct" :disabled="isLoading">
          <i class="fas fa-sync-alt" :class="{ 'fa-spin': isLoading }"></i>
          <span>Aggiorna</span>
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="sci-main-content">
      <!-- KPI Bento Row -->
      <section class="kpi-bento-grid">
        <!-- Total Warehouse Value -->
        <div class="sci-card kpi-card kpi-value">
          <div class="kpi-icon-box value-icon">
            <i class="fas fa-coins"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Valore Totale Inventario</span>
            <span class="kpi-number font-monospace">{{ totaleMagazzino }} €</span>
          </div>
        </div>

        <!-- Total Products Count -->
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box total-icon">
            <i class="fas fa-layer-group"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Articoli a Catalogo</span>
            <span class="kpi-number">{{ prodotti.length }}</span>
          </div>
        </div>

        <!-- Low Stock Alert -->
        <div class="sci-card kpi-card" :class="{ 'kpi-alert-card': sottoScortaCount > 0 }">
          <div class="kpi-icon-box warning-icon">
            <i class="fas fa-exclamation-triangle"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">Articoli Sotto Scorta</span>
            <span class="kpi-number">{{ sottoScortaCount }}</span>
          </div>
        </div>

        <!-- To Order Count -->
        <div class="sci-card kpi-card">
          <div class="kpi-icon-box order-icon">
            <i class="fas fa-cart-arrow-down"></i>
          </div>
          <div class="kpi-info">
            <span class="kpi-label">In Ordine Fornitore</span>
            <span class="kpi-number">{{ daOrdinareCount }}</span>
          </div>
        </div>
      </section>

      <!-- Toolbar & Filters -->
      <div class="sci-toolbar-card">
        <div class="toolbar-filter-pills">
          <button 
            class="filter-pill" 
            :class="{ active: stockFilter === 'ALL' }"
            @click="stockFilter = 'ALL'"
          >
            Tutti ({{ prodotti.length }})
          </button>
          <button 
            class="filter-pill pill-warning" 
            :class="{ active: stockFilter === 'LOW' }"
            @click="stockFilter = 'LOW'"
          >
            <i class="fas fa-exclamation-circle me-1"></i>
            Sotto Scorta ({{ sottoScortaCount }})
          </button>
          <button 
            class="filter-pill pill-order" 
            :class="{ active: stockFilter === 'ORDER' }"
            @click="stockFilter = 'ORDER'"
          >
            <i class="fas fa-shopping-bag me-1"></i>
            Da Ordinare ({{ daOrdinareCount }})
          </button>
        </div>

        <div class="toolbar-search-wrap">
          <i class="fas fa-search search-icon"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Cerca per codice o nome prodotto..." 
            class="sci-search-input"
          />
          <button v-if="searchQuery" class="clear-btn" @click="searchQuery = ''">&times;</button>
        </div>
      </div>

      <!-- Inventory Table Card -->
      <section class="sci-card table-card">
        <div class="table-responsive">
          <table class="sci-table">
            <thead>
              <tr>
                <th>Prodotto & Codice</th>
                <th style="width: 170px;">Giacenza Attuale (gr/pz)</th>
                <th style="width: 170px;">Scorta Minima</th>
                <th style="width: 170px;">Da Ordinare</th>
                <th style="width: 160px;">Prezzo (€/100gr)</th>
                <th style="width: 150px; text-align: right;">Valore Riga</th>
                <th style="width: 80px; text-align: center;">Azioni</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="(prodotto, index) in filteredProdotti" 
                :key="prodotto.code ? prodotto.code + '_' + index : index"
                class="sci-table-row"
                :class="{ 'row-understock': isUnderStock(prodotto) }"
              >
                <!-- Product / Code -->
                <td>
                  <div class="product-cell">
                    <span class="product-name">{{ prodotto.descrizio || prodotto.nome || 'Senza Descrizione' }}</span>
                    <span class="product-code font-monospace" v-if="prodotto.code">
                      <i class="fas fa-barcode me-1"></i> {{ prodotto.code }}
                    </span>
                  </div>
                </td>

                <!-- Current Stock Quantity -->
                <td>
                  <div class="table-input-wrap">
                    <input 
                      type="number" 
                      min="0" 
                      v-model.number="prodotto.quantita" 
                      class="sci-table-num-input font-monospace"
                      @change="handleStockChange(prodotto)"
                    />
                    <span v-if="isUnderStock(prodotto)" class="badge-stock-alert" title="Scorta inferiore alla soglia minima">
                      <i class="fas fa-arrow-down"></i>
                    </span>
                  </div>
                </td>

                <!-- Minimum Threshold -->
                <td>
                  <input 
                    type="number" 
                    min="0" 
                    v-model.number="prodotto.quantitamin" 
                    class="sci-table-num-input font-monospace"
                    @change="handleMinStockChange(prodotto)"
                  />
                </td>

                <!-- To Order Quantity -->
                <td>
                  <input 
                    type="number" 
                    min="0" 
                    v-model.number="prodotto.quantitaordine" 
                    class="sci-table-num-input font-monospace"
                    @change="handleToOrderChange(prodotto)"
                  />
                </td>

                <!-- Unit Price (€/100gr) -->
                <td>
                  <div class="sci-currency-field">
                    <span class="currency-symbol">€</span>
                    <input 
                      type="number" 
                      step="0.01" 
                      min="0" 
                      v-model.number="prodotto.prezzo" 
                      class="sci-currency-input font-monospace"
                      @change="handlePriceChange(prodotto)"
                    />
                  </div>
                </td>

                <!-- Row Value Calculation -->
                <td style="text-align: right;">
                  <span class="row-total-val font-monospace">
                    {{ computeRowTotal(prodotto) }} €
                  </span>
                </td>

                <!-- Actions: Delete with confirmation -->
                <td style="text-align: center;">
                  <button 
                    class="btn-delete-prod" 
                    title="Elimina prodotto dal magazzino"
                    @click="deleteProduct(prodotto.code)"
                  >
                    <i class="fas fa-trash-alt"></i>
                  </button>
                </td>
              </tr>

              <!-- Empty State -->
              <tr v-if="filteredProdotti.length === 0">
                <td colspan="7" class="sci-empty-state">
                  <div class="empty-content">
                    <div class="empty-icon-halo">
                      <i class="fas fa-box-open"></i>
                    </div>
                    <h3 class="empty-title">Nessun prodotto trovato</h3>
                    <p class="empty-desc">
                      {{ searchQuery ? 'Nessun articolo corrisponde ai filtri di ricerca impostati.' : 'Non ci sono ancora prodotti registrati nel magazzino.' }}
                    </p>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </main>

    <!-- Success / Error Floating Toast -->
    <transition name="fade">
      <div class="sci-floating-toast" v-if="feedbackMsg">
        <i class="fas fa-check-circle text-success me-2"></i>
        <span>{{ feedbackMsg }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import { store } from '../store/store.js'

export default {
  name: 'Magazzino',
  data() {
    return {
      prodotti: [],
      searchQuery: '',
      stockFilter: 'ALL',
      isLoading: false,
      feedbackMsg: '',
      store
    }
  },
  computed: {
    sottoScortaCount() {
      return this.prodotti.filter(p => this.isUnderStock(p)).length;
    },
    daOrdinareCount() {
      return this.prodotti.filter(p => (parseFloat(p.quantitaordine) || 0) > 0).length;
    },
    filteredProdotti() {
      if (!this.prodotti || !Array.isArray(this.prodotti)) return [];
      let list = this.prodotti;

      if (this.stockFilter === 'LOW') {
        list = list.filter(p => this.isUnderStock(p));
      } else if (this.stockFilter === 'ORDER') {
        list = list.filter(p => (parseFloat(p.quantitaordine) || 0) > 0);
      }

      if (this.searchQuery && this.searchQuery.trim() !== '') {
        const q = this.searchQuery.toLowerCase().trim();
        list = list.filter(p => {
          const desc = String(p.descrizio || '').toLowerCase();
          const nome = String(p.nome || '').toLowerCase();
          const code = String(p.code || '').toLowerCase();
          return desc.includes(q) || nome.includes(q) || code.includes(q);
        });
      }

      return list;
    },
    totaleMagazzino() {
      if (!this.prodotti || !Array.isArray(this.prodotti)) return '0.00';
      const total = this.prodotti.reduce((acc, p) => {
        const qta = parseFloat(p.quantita) || 0;
        const prezzo = parseFloat(p.prezzo) || 0;
        return acc + (qta * (prezzo / 100));
      }, 0);
      return total.toFixed(2);
    }
  },
  methods: {
    getToken() {
      try {
        if (!localStorage.token) return localStorage.getItem('token') || '';
        const parsed = JSON.parse(localStorage.token);
        return typeof parsed === 'string' ? parsed : (localStorage.getItem('token') || '');
      } catch (e) {
        return localStorage.getItem('token') || '';
      }
    },
    home() {
      this.$router.push({ name: "Gestionale" });
    },
    isUnderStock(p) {
      if (!p) return false;
      const qta = parseFloat(p.quantita) || 0;
      const min = parseFloat(p.quantitamin) || 0;
      return min > 0 && qta <= min;
    },
    computeRowTotal(p) {
      if (!p) return '0.00';
      const qta = parseFloat(p.quantita) || 0;
      const prezzo = parseFloat(p.prezzo) || 0;
      return (qta * (prezzo / 100)).toFixed(2);
    },
    displayProduct() {
      this.isLoading = true;
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;

      this.axios.post(this.store.path + 'displayProduct', {
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then((response) => {
        console.log("Magazzino displayProduct response:", response.data);
        const dataList = Array.isArray(response.data)
          ? response.data
          : (response.data && response.data.messaggio ? response.data.messaggio : []);

        const loaded = [];
        dataList.forEach(element => {
          element.prezzo = element.prezzo ? Number(element.prezzo) : 0;
          element.quantita = element.quantita !== undefined && element.quantita !== null ? Number(element.quantita) : 0;
          element.quantitamin = element.quantitamin ? Number(element.quantitamin) : 0;
          element.quantitaordine = element.quantitaordine ? Number(element.quantitaordine) : 0;
          element.stato = element.stato ? element.stato : '';
          loaded.push(element);
        });
        this.prodotti = loaded;
      }).catch(err => {
        console.error('Errore caricamento magazzino:', err);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    handleStockChange(p) {
      this.updateQuantita(p.code, p.quantita, null, null);
    },
    handleMinStockChange(p) {
      this.updateQuantita(p.code, null, p.quantitamin, null);
    },
    handleToOrderChange(p) {
      this.updateQuantita(p.code, null, null, p.quantitaordine);
    },
    handlePriceChange(p) {
      this.updatePrezzo(p.code, p.prezzo);
    },
    updateQuantita(code, quantita, quantitamin, quantitaordine) {
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;
      this.axios.post(this.store.path + 'updateQuantita', {
        code: code,
        quantita: quantita,
        quantitamin: quantitamin,
        quantitaordine: quantitaordine,
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then(() => {
        this.showToast('Giacenza aggiornata');
      }).catch(err => {
        console.error('Errore update quantita:', err);
      });
    },
    updatePrezzo(code, prezzo) {
      const token = this.getToken();
      const authHeader = `Bearer ${token}`;
      this.axios.post(this.store.path + 'updatePrezzo', {
        code: code,
        prezzo: prezzo,
        headers: {
          authorization: authHeader
        }
      }, {
        headers: {
          authorization: authHeader
        }
      }).then(() => {
        this.showToast('Prezzo aggiornato');
      }).catch(err => {
        console.error('Errore update prezzo:', err);
      });
    },
    async deleteProduct(code) {
      if (!confirm('Sei sicuro di voler eliminare questo prodotto dal magazzino?')) {
        return;
      }
      try {
        const token = this.getToken();
        const authHeader = `Bearer ${token}`;
        const response = await this.axios.post(this.store.path + 'deleteProduct', {
          code: code,
          headers: {
            authorization: authHeader
          }
        }, {
          headers: {
            authorization: authHeader
          }
        });
        if (response.data && response.data.messaggio) {
          this.prodotti = this.prodotti.filter(p => p.code !== code);
          this.showToast('Prodotto eliminato con successo');
        }
      } catch (error) {
        console.error('Errore eliminazione:', error);
        alert('Errore durante l\'eliminazione del prodotto');
      }
    },
    showToast(msg) {
      this.feedbackMsg = msg;
      setTimeout(() => {
        if (this.feedbackMsg === msg) this.feedbackMsg = '';
      }, 2000);
    }
  },
  mounted() {
    this.displayProduct();
  }
}
</script>

<style scoped>
.sci-view-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--sci-bg, #f8fafc);
  color: var(--sci-text-main, #0f172a);
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  padding-bottom: 50px;
}

/* Header */
.sci-header {
  position: sticky;
  top: 0;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px 28px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.04);
}

.sci-header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.sci-btn-back {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05);
}

.sci-btn-back:hover {
  background: #f1f5f9;
  border-color: #94a3b8;
  color: #0f172a;
  transform: translateX(-2px);
}

.sci-title-group {
  display: flex;
  flex-direction: column;
}

.sci-title {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
}

.sci-subtitle {
  margin: 2px 0 0 0;
  font-size: 0.85rem;
  color: #64748b;
}

.sci-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* Main Content */
.sci-main-content {
  max-width: 1440px;
  margin: 24px auto;
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* KPI Bento Grid */
.kpi-bento-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 16px;
}

.kpi-card {
  padding: 20px;
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 2px 10px rgba(0,0,0,0.03);
  display: flex;
  align-items: center;
  gap: 16px;
  transition: transform 0.2s;
}

.kpi-card:hover {
  transform: translateY(-2px);
}

.kpi-icon-box {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}

.value-icon { background: #ecfdf5; color: #059669; }
.total-icon { background: #eff6ff; color: #2563eb; }
.warning-icon { background: #fffbeb; color: #d97706; }
.order-icon { background: #f5f3ff; color: #7c3aed; }

.kpi-alert-card {
  border-color: #fde68a;
  background: #fffdf5;
}

.kpi-info {
  display: flex;
  flex-direction: column;
}

.kpi-label {
  font-size: 0.82rem;
  color: #64748b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.kpi-number {
  font-size: 1.45rem;
  font-weight: 800;
  color: #0f172a;
}

/* Toolbar Card */
.sci-toolbar-card {
  background: #ffffff;
  border: 1px solid var(--sci-border, #e2e8f0);
  border-radius: 14px;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.03);
}

.toolbar-filter-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-pill {
  min-height: 40px;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.88rem;
  font-weight: 600;
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
}

.filter-pill:hover {
  background: #f8fafc;
}

.filter-pill.active {
  background: #0f172a;
  color: #ffffff;
  border-color: #0f172a;
}

.filter-pill.pill-warning.active {
  background: #d97706;
  border-color: #d97706;
}

.filter-pill.pill-order.active {
  background: #7c3aed;
  border-color: #7c3aed;
}

.toolbar-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 12px;
  color: #94a3b8;
  font-size: 0.9rem;
}

.sci-search-input {
  min-height: 40px;
  padding: 6px 30px 6px 34px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 0.9rem;
  background: #ffffff;
  color: #0f172a;
  min-width: 260px;
}

.clear-btn {
  position: absolute;
  right: 10px;
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
}

/* Table Card */
.table-card {
  background: #ffffff;
  border-radius: 16px;
  border: 1px solid var(--sci-border, #e2e8f0);
  box-shadow: 0 4px 20px -2px rgba(0, 0, 0, 0.05);
  overflow: hidden;
}

.sci-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  text-align: left;
}

.sci-table thead th {
  background: #f8fafc;
  color: #475569;
  font-size: 0.85rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 14px 20px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 10;
}

.sci-table-row td {
  padding: 12px 20px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
  transition: background 0.15s;
}

.sci-table-row:hover td {
  background: #f8fafc;
}

.sci-table-row.row-understock td {
  background: #fffdf5;
}

.product-cell {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.product-name {
  font-weight: 600;
  color: #0f172a;
  font-size: 0.95rem;
}

.product-code {
  font-size: 0.8rem;
  color: #64748b;
}

.table-input-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.sci-table-num-input {
  width: 110px;
  min-height: 38px;
  padding: 6px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;
}

.sci-table-num-input:focus {
  outline: none;
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.badge-stock-alert {
  color: #d97706;
  background: #fef3c7;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
}

.sci-currency-field {
  display: inline-flex;
  align-items: center;
  position: relative;
  width: 120px;
}

.currency-symbol {
  position: absolute;
  left: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748b;
  pointer-events: none;
}

.sci-currency-input {
  width: 100%;
  min-height: 38px;
  padding: 6px 8px 6px 26px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 0.92rem;
  font-weight: 600;
  color: #0f172a;
  background: #ffffff;
  outline: none;
}

.sci-currency-input:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.15);
}

.row-total-val {
  font-size: 0.98rem;
  font-weight: 700;
  color: #059669;
}

.btn-delete-prod {
  background: none;
  border: 1px solid #fecaca;
  color: #ef4444;
  border-radius: 8px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-delete-prod:hover {
  background: #fee2e2;
  color: #b91c1c;
}

/* Floating Toast */
.sci-floating-toast {
  position: fixed;
  bottom: 24px;
  right: 28px;
  background: #0f172a;
  color: #ffffff;
  padding: 12px 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.25);
  font-weight: 600;
  font-size: 0.9rem;
  z-index: 1100;
  display: flex;
  align-items: center;
}

/* Buttons */
.sci-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 18px;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  text-decoration: none;
}

.sci-btn-primary {
  background: #10b981;
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.25);
}

.sci-btn-primary:hover:not(:disabled) {
  background: #059669;
  transform: translateY(-1px);
}

.sci-btn-secondary {
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
}

.sci-btn-secondary:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}

/* Empty State */
.sci-empty-state {
  padding: 60px 24px;
  text-align: center;
}

.empty-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 400px;
  margin: 0 auto;
}

.empty-icon-halo {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin-bottom: 16px;
}

.empty-title {
  margin: 0 0 6px 0;
  font-size: 1.2rem;
  font-weight: 700;
  color: #1e293b;
}

.empty-desc {
  margin: 0;
  font-size: 0.9rem;
  color: #64748b;
}
</style>
